import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import GettingStarted from './components/GettingStarted';
import HelpCenter from './components/HelpCenter';
import HomePage from './components/HomePage';
import TaskManager from './components/TaskManager';
import ApiDemo from './components/ApiDemo';
import SettingsPage from './components/SettingsPage';
import AuditLogPage from './components/AuditLogPage';
import LoginPage from './components/LoginPage';
import AcceptInvitationPage from './components/AcceptInvitationPage';
import EmailVerificationPage from './components/EmailVerificationPage';
import SSOCallbackPage from './components/SSOCallbackPage';
import FirebaseTest from './components/FirebaseTest';
import DatabaseTest from './components/DatabaseTest';
import ComplianceImport from './components/ComplianceImport';
import ComplianceManagement from './components/ComplianceManagement';
import AuditExecutionView from './components/AuditExecutionView';
import LocationManagement from './components/LocationManagement';
import FloorMapView from './components/FloorMapView';
import FloorMapFullView from './components/FloorMapFullView';
import EvidenceVault from './components/EvidenceVault';
import ProtectedRoute from './components/ProtectedRoute';
import FeatureProtectedRoute from './components/FeatureProtectedRoute';
import AuthTest from './components/AuthTest';
import UserManagement from './components/UserManagement';
import ChecklistManagement from './components/ChecklistManagement';
import FormManagement from './components/FormManagement';
import AgreementManagement from './components/AgreementManagement';
import OrganizationManagement from './components/OrganizationManagement';
import AssetManagement from './components/AssetManagement';
import AssetRecordPage from './components/AssetRecordPage';
import SubscriptionSuccessPage from './components/SubscriptionSuccessPage';
import SubscriptionManagement from './components/SubscriptionManagement';
import SubscriptionDiagnostics from './components/SubscriptionDiagnostics';
import MobileAppDownload from './components/MobileAppDownload';
import MobileAppDownloadRoute from './components/MobileAppDownloadRoute';
import GetStartedPage from './components/GetStartedPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsPage from './components/TermsPage';
import SecurityPage from './components/SecurityPage';
import AboutUsPage from './components/AboutUsPage';
import MessagesPage from './components/MessagesPage';
import ChatGPTIntegrationInstructions from './components/ChatGPTIntegrationInstructions';
import AdminSupportDashboard from './components/AdminSupportDashboard';
import { TaskProvider } from './contexts/TaskContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { PreviewProvider } from './contexts/PreviewContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { LocationFilterProvider } from './contexts/LocationFilterContext';
import { getLoginUrl } from './utils/domainUtils';
import MarketingPage from './components/MarketingPage';
import './utils/getFirebaseToken'; // Expose getFirebaseToken to window for console access
import { setupAuthStateListener, cleanupAuthStateListener } from './utils/firebaseErrorHandler';
import PageViewTracker from './components/PageViewTracker';
import { trackEngagement } from './utils/analytics';
import { setupForegroundMessageListener } from './services/fcmService';
import { useOrganizationType } from './hooks/useOrganizationType';
import { playNotificationSound, showMessageNotification, updateBrowserTabBadge } from './utils/messageNotifications';

/** Floor map routes: gate by org type so Bank (etc.) never loads floor map data. */
const FloorMapRoute: React.FC = () => {
  const cfg = useOrganizationType();
  if (cfg.menuItems['floor-map'] === false) {
    return <Navigate to="/home" replace />;
  }
  return <FloorMapView />;
};

const FloorMapFullRoute: React.FC = () => {
  const cfg = useOrganizationType();
  if (cfg.menuItems['floor-map'] === false) {
    return <Navigate to="/home" replace />;
  }
  return <FloorMapFullView />;
};

// Component to handle landing pages
// In production: Firebase Hosting serves /landing/* as static files before React Router
// In development: Redirects to marketing app dev server or shows message
const LandingPageRedirect: React.FC = () => {
  React.useEffect(() => {
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isDevelopment) {
      // In development, try to redirect to marketing app dev server
      // Marketing app runs on port 5174
      const marketingPort = '5174';
      const currentPath = window.location.pathname;
      const marketingUrl = `http://localhost:${marketingPort}${currentPath}`;
      
      // Try to load from marketing dev server, fall back to showing message
      window.location.href = marketingUrl;
    } else {
      // In production, the static files should already be served by Firebase Hosting
      // If we reach here, it means the files aren't built/deployed
      console.warn('Landing page not found. Make sure marketing app is built and deployed.');
    }
  }, []);
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontSize: '16px',
      color: '#666',
      padding: '20px',
      textAlign: 'center'
    }}>
      <div>Loading landing page...</div>
      <div style={{ marginTop: '10px', fontSize: '14px', color: '#999' }}>
        {window.location.hostname === 'localhost' && (
          <div>Make sure marketing app is running: <code>cd marketing && npm run dev</code></div>
        )}
      </div>
    </div>
  );
};

// Component to handle root path - show marketing for unauthenticated, redirect authenticated to /home
// This component must be defined inside the provider structure
const RootRedirect: React.FC = () => {
  // This will be called inside AuthProvider, so useAuth should work
  const { isAuthenticated } = useAuth();
  
  if (isAuthenticated) {
    // Authenticated users on root - redirect to /home
    return <Navigate to="/home" replace />;
  }
  
  // Unauthenticated users on root - show marketing page
  return <MarketingPage />;
};

// Component to handle session timeout events
const SessionTimeoutHandler: React.FC = () => {
  const { logout } = useAuth();

  useEffect(() => {
    const handleSessionTimeout = () => {
      console.log('🔄 Session timeout event received, logging out...');
      logout();
      window.location.href = getLoginUrl();
    };

    window.addEventListener('session_timeout', handleSessionTimeout);
    
    return () => {
      window.removeEventListener('session_timeout', handleSessionTimeout);
    };
  }, [logout]);

  return null;
};

// Component to set up global FCM listener (must be inside AuthProvider)
const GlobalFCMListener: React.FC = () => {
  const { isAuthenticated } = useAuth();

  // Set up global FCM foreground message listener for push notifications
  // This ensures notifications work on all pages, not just MessagesPage
  useEffect(() => {
    if (!isAuthenticated) return;

    console.log('🔔 Setting up global FCM message listener...');
    
    try {
      setupForegroundMessageListener((payload) => {
        console.log('📬 FCM foreground message received globally:', payload);
        
        // Play sound
        playNotificationSound();
        
        // Update browser tab badge
        updateBrowserTabBadge(1);
        
        // Show browser notification if permission is granted
        if (Notification.permission === 'granted' && payload.notification) {
          const notification = payload.notification;
          const conversationId = payload.data?.conversationId;
          
          // Show notification with proper title and body
          showMessageNotification(
            notification.title || 'New Message',
            notification.body || '',
            conversationId
          );
        }
      });

      console.log('✅ Global FCM message listener set up');
    } catch (error) {
      console.warn('⚠️ Failed to set up global FCM message listener:', error);
    }
  }, [isAuthenticated]);

  return null;
};

const App: React.FC = () => {
  // Set up Firebase Auth state listener on app mount
  useEffect(() => {
    setupAuthStateListener();
    return () => {
      cleanupAuthStateListener();
    };
  }, []);

  // Track engagement metrics periodically (every 5 minutes)
  useEffect(() => {
    // Track immediately
    trackEngagement();
    
    // Then track every 5 minutes
    const interval = setInterval(() => {
      trackEngagement();
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <AuthProvider>
      <PreviewProvider>
        <OrganizationProvider>
          <SubscriptionProvider>
          <NotificationProvider>
            <LocationFilterProvider>
            <TaskProvider>
              <GlobalFCMListener />
              <Router>
                <SessionTimeoutHandler />
                <PageViewTracker />
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sso-callback" element={<SSOCallbackPage />} />
            {/* Firebase auth handler - redirects here after OAuth */}
            <Route path="/__/auth/handler" element={<SSOCallbackPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/about" element={<AboutUsPage />} />
            
            {/* Public download route - no authentication required */}
            <Route path="/download/mobile-app" element={<MobileAppDownloadRoute />} />
            
            {/* Landing pages - bypass React Router, let Firebase Hosting serve static files */}
            <Route path="/landing/*" element={<LandingPageRedirect />} />
            
            {/* Root path - redirects authenticated users to /home, unauthenticated to login */}
            <Route path="/" element={<RootRedirect />} />
            
            {/* Protected Routes - all app routes */}
            <Route element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route path="home" element={<HomePage />} />
              <Route path="tasks" element={<TaskManager />} />
              <Route path="user-management" element={<UserManagement />} />
              <Route path="subscription" element={<SubscriptionManagement />} />
              <Route path="api-demo" element={<ApiDemo />} />
              <Route path="firebase-test" element={<FirebaseTest />} />
              <Route path="database-test" element={<DatabaseTest />} />
              <Route path="auth-test" element={<AuthTest />} />
              <Route path="compliance-import" element={<ComplianceImport />} />
              <Route path="compliance-management" element={<ComplianceManagement />} />
              <Route path="audit/:auditId" element={<AuditExecutionView />} />
              <Route path="checklists" element={<ChecklistManagement />} />
              <Route path="forms" element={<FormManagement />} />
              <Route path="agreements" element={<AgreementManagement />} />
              <Route path="locations" element={<LocationManagement />} />
              <Route path="floor-map" element={<FloorMapRoute />} />
              <Route path="assets" element={<AssetManagement />} />
              <Route path="asset/:assetId" element={<AssetRecordPage />} />
              <Route path="evidence-vault" element={<EvidenceVault />} />
              <Route path="organizations" element={<OrganizationManagement />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="chatgpt-integration" element={<ChatGPTIntegrationInstructions />} />
              <Route path="mobile-app" element={<MobileAppDownload />} />
              <Route path="help" element={<HelpCenter />} />
              <Route path="help/getting-started" element={<GettingStarted />} />
              <Route path="subscription-diagnostics" element={<SubscriptionDiagnostics />} />
              <Route path="admin-support" element={<AdminSupportDashboard />} />
            </Route>
            
            {/* Floor Map Full View - Standalone full-screen (no Layout), opens in new tab */}
            <Route path="floor-map/full" element={
              <ProtectedRoute>
                <FloorMapFullRoute />
              </ProtectedRoute>
            } />

            {/* Messages Page - Standalone (no Layout wrapper, opens in new tab) */}
            <Route path="messages" element={
              <ProtectedRoute>
                <FeatureProtectedRoute feature="messaging">
                  <MessagesPage />
                </FeatureProtectedRoute>
              </ProtectedRoute>
            } />
            
            {/* Redirect any unknown routes to home */}
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </Router>
            </TaskProvider>
            </LocationFilterProvider>
          </NotificationProvider>
          </SubscriptionProvider>
        </OrganizationProvider>
      </PreviewProvider>
    </AuthProvider>
  );
};

export default App;
